<template lang="pug">
  .mr-password-input
    mrInput(
      v-model="password"
      :data-mr-ass="dataMrAss"
      :v="selectedPasswordValidations"
      :label="label"
      :input-type="showPassword ? 'text' : 'password'"
      :required="required && !isInPasswordSuggestionExperiment"
      :autocomplete="autocomplete"
      :name='name'
    )
    mrInput(v-if="showConfirmPassword" :model-value='confirmPassword' @update:modelValue="(value) => $emit('confirm-password-input', value)" :v="v$.confirmPassword" input-type='password' label="Confirm Password" required="true" :dataMrAss="'confirm-new-password'")

    template(v-if="isInPasswordSuggestionExperiment")
      span.show-password.upper.clickable.color-purple.font-small(
        v-show="password"
        @click="showPassword = !showPassword"
      ) {{ showPassword ? 'hide' : 'show' }}

      .validations-status
        .validation-status(v-for="status in customCheckeablePasswordValidationStatus" :key="status.key" :class="{ 'color-green': status.valid, 'text-color-3': !status.valid }")
          MrIcon(name="check-circle-solid" v-show="status.valid")
          MrIcon(name="times-circle-solid" v-show="!status.valid")
          span.detail-text.xs-f-xxsmall {{status.text}}
</template>

<script>
  import { mapState } from 'vuex';
  import { required, sameAs } from '@vuelidate/validators';
  import MrInput from '@components/mrInput';
  import MrIcon from "@components/MrIcon";
  import { useVuelidate } from "@vuelidate/core";

  export default {

    name: 'MrPasswordInput',

    components: {
      MrInput,
      MrIcon,
    },

    props: {
      modelValue: {
        type: String,
        default: null
      },
      required: {
        type: Boolean,
        default: false,
      },
      dataMrAss: {
        type: String,
        default: 'password',
      },
      autocomplete: {
        type: String,
        default: ""
      },
      name: {
        type: String,
        default: '',
      },
      label: {
        type: String,
        default: "Password"
      },
      showConfirmPassword: {
        type: Boolean,
        default: false
      },
      confirmPassword: {
        type: String,
        default: null
      },
    },

    data: () => ({
      showPassword: false,
      password: null,
    }),

    emits: ['update:modelValue', 'update:valid', 'confirm-password-input'],

    setup () {
      return {
        v$: useVuelidate()
      };
    },

    computed: {
      ...mapState('signIn', [
        'passwordValidators',
      ]),

      selectedPasswordValidations () {
        if (!this.experiments || !Object.keys(this.experiments).length || this.isInPasswordSuggestionExperiment) {
          return {};
        }

        return this.v$.password;
      },

      isInPasswordSuggestionExperiment() {
        return this.experiments && this.experiments['Password Suggestions'] === 'B';
      },

      customCheckeablePasswordValidationStatus() {
        let passwordValidationObj = this.v$.password;

        if (!passwordValidationObj || passwordValidationObj.required === undefined) {
          return [];
        }

        return [{
                  valid: !passwordValidationObj.minLength?.$invalid && !passwordValidationObj.maxLength?.$invalid && !passwordValidationObj.required?.$invalid,
                  text: `Must be between ${passwordValidationObj.minLength.$params.min} and ${passwordValidationObj.maxLength.$params.max} characters`,
                  key: 'minMaxLength',
                },
                {
                  valid: !passwordValidationObj.containsUpperAndLowerCaseCharacters?.$invalid,
                  text: `Must contain 1 upper and 1 lower case character`,
                  key: 'containsUpperAndLowerCaseCharacters',
                },
                {
                  valid: !passwordValidationObj.containsNumber?.$invalid,
                  text: `Must contain numbers`,
                  key: 'containsNumber',
                },
                {
                  valid: !passwordValidationObj.containsSymbol?.$invalid,
                  text: `Must contain symbols (!@#$%^&*()?][{}+_-=\`~)`,
                  key: 'containsSymbol',
                },
        ];
      },
    },

    watch: {
      password: {
        immediate: true,
        handler(newValue, oldValue) {
          if (this.isInPasswordSuggestionExperiment || !(newValue === null && oldValue === undefined)) {
            this.v$.$touch();
          }

          this.$emit('update:modelValue', this.password);
          this.$emit('update:valid', !this.v$.$invalid);
        },
      },

      modelValue: {
        immediate: true,
        handler(newValue, oldValue) {
          if (newValue !== oldValue && newValue !== this.password) {
            this.password = newValue;
          }
        },
      },
    },

    validations() {
      let validations = {};

      if (this.required) {
        validations.password = this.passwordValidators;
      }

      if (this.showConfirmPassword) {
        validations.confirmPassword = {
          required,
          passwordSameAs: sameAs(this.password)
        };
      }

      return validations;
    },
  };
</script>

<style lang="stylus">
  .mr-password-input
    position relative

    .show-password
      position absolute
      top 0
      right 0
      padding 11px

    .validations-status
      padding-bottom 1em
      display flex
      flex-direction column
      justify-content center

      .validation-status
        position relative
        display flex
        align-items flex-start
        margin-bottom 8px
        padding-left 13px

        &:last-child
          margin-bottom 0px

        .mr-icon
          position absolute
          top 50%
          left 0
          max-width 12px
          margin-top -6px

          @media mq-mobile
            margin-top 1vw

        .detail-text
          margin-left 0.29em
</style>
